/* eslint-disable import/no-extraneous-dependencies */
import React, { useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TimeZoneContext } from '@contexts/timezone-context';
import { TenantContext } from '@contexts/tenant-context';
import { ModalContext } from '@contexts/modal-context';
import { CalendarContext } from '../../CalendarContext';
const DEFAULT_TIMEZONE_CONTEXT = {
    timeZonesForSelect: [
        { label: 'Eastern Time (US & Canada)', value: 'America/New_York' },
        { label: 'Central Time (US & Canada)', value: 'America/Chicago' },
        { label: 'Pacific Time (US & Canada)', value: 'America/Los_Angeles' }
    ],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    timeZonesMapping: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'America/New_York': 'Eastern Time (US & Canada)',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'America/Chicago': 'Central Time (US & Canada)',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'America/Los_Angeles': 'Pacific Time (US & Canada)'
    },
    timezone: 'America/New_York',
    method: 'dynamic',
    setTimeZone: () => undefined,
    setMethod: () => undefined
};
const DEFAULT_TENANT_CONTEXT = {
    tenant: {
        id: 1,
        name: 'Mock Tenant',
        subdomain: 'mock',
        flags: {
            enableGenericEvents: true,
            hasMultiTimezone: false
        }
    }
};
const DEFAULT_MODAL_CONTEXT = {
    activeModal: null,
    modifiedEventWatch: null,
    createdEventsWatch: null,
    deletedEventsWatch: null,
    modifiedEventsWatch: null,
    openVisitModal: () => undefined,
    openTimeOffModal: () => undefined,
    openNewGenericEventModal: () => undefined,
    openEditGenericEventModal: () => undefined
};
const DEFAULT_FILTER_STATE = {
    values: {
        building: '',
        technicianTeam: '',
        territory: '',
        tags: [],
        buildingTags: '',
        accountTags: '',
        workOrderTags: '',
        type: '',
        inspectionStatus: '',
        inspectionType: '',
        visitStatus: '',
        asset: '',
        inspectionStatusKeys: [],
        visitStatusKeys: [],
        additionalTerritory: ''
    },
    active: {
        building: false,
        technicianTeam: false,
        territory: false,
        tags: false,
        buildingTags: false,
        accountTags: false,
        workOrderTags: false,
        type: false,
        inspectionStatus: false,
        inspectionType: false,
        visitStatus: false,
        asset: false,
        statuses: false,
        additionalTerritory: false
    }
};
const DEFAULT_PERMISSIONS = {
    settings: true,
    techs: true,
    statuses: true,
    hasService: true,
    createEvent: true,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    techs_time_offs: true
};
export function buildMockCalendarContextValue(overrides = {}) {
    const defaultState = {
        events: [],
        technicians: [
            { label: 'Alex Thompson', value: 1 },
            { label: 'Jordan Lee', value: 2 },
            { label: 'Sam Rivera', value: 3 }
        ],
        selectedTechnicianIds: [],
        selectedSubcontractorIds: [],
        errorMessage: '',
        loading: false,
        detailed: false,
        filters: DEFAULT_FILTER_STATE,
        unselectedTechs: {},
        view: 'dayGridMonth',
        statuses: {},
        modalOpen: false
    };
    const defaultActions = {
        onDateRangeChange: () => undefined,
        onToggleStatus: () => undefined,
        toggleSelectAllStatuses: () => undefined,
        toggleUnselectAllStatuses: () => undefined,
        toggleUnselectAllInspectionStatuses: () => undefined,
        toggleUnselectAllVisitStatuses: () => undefined,
        toggleSelectAllTechs: () => undefined,
        toggleUnselectAllTechs: () => undefined,
        onToggleTech: () => () => undefined,
        setTechnicians: () => undefined,
        setSubcontractors: () => undefined,
        onChangeView: () => undefined,
        onResetFilters: () => undefined,
        onChangeSearchFilter: () => () => undefined,
        handleMultiSelectFilterChange: () => () => undefined,
        onChangeReactFilter: () => () => undefined,
        onToggleFilter: () => () => undefined,
        onTypeFilterChange: () => () => undefined,
        onTypeFilterToggle: () => () => undefined,
        reloadEvents: () => undefined,
        saveVisit: () => undefined,
        saveGenericEvent: () => undefined,
        setIncludeInactiveTechs: () => undefined
    };
    const defaultSelectOptions = [];
    const defaultVisitTypeOptions = [];
    return Object.assign({ assetsForSelect: defaultSelectOptions, visitTypesForSelect: defaultVisitTypeOptions, territoriesForSelect: defaultSelectOptions, inspectionTypesForSelect: defaultSelectOptions, inspectionStatusesForSelect: defaultSelectOptions, visitStatusesForSelect: defaultSelectOptions, visitStatusesForHighlight: defaultSelectOptions, techniciansForSelect: [
            { label: 'Alex Thompson', value: 1 },
            { label: 'Jordan Lee', value: 2 },
            { label: 'Sam Rivera', value: 3 }
        ], inactiveTechniciansForSelect: [], includeInactiveTechs: false, permissions: DEFAULT_PERMISSIONS, state: defaultState, actions: defaultActions }, overrides);
}
export function createTestQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                retry: false,
                cacheTime: 0
            }
        }
    });
}
export function MockCalendarProviders({ children, calendarContextValue, modalContextValue, timeZoneContextValue, tenantContextValue, queryClient }) {
    const qc = useMemo(() => queryClient !== null && queryClient !== void 0 ? queryClient : createTestQueryClient(), [queryClient]);
    const tzContext = useMemo(() => (Object.assign(Object.assign({}, DEFAULT_TIMEZONE_CONTEXT), timeZoneContextValue)), [timeZoneContextValue]);
    const tenantCtx = useMemo(() => (Object.assign(Object.assign({}, DEFAULT_TENANT_CONTEXT), tenantContextValue)), [tenantContextValue]);
    const modalCtx = useMemo(() => (Object.assign(Object.assign({}, DEFAULT_MODAL_CONTEXT), modalContextValue)), [modalContextValue]);
    const calendarCtx = useMemo(() => buildMockCalendarContextValue(calendarContextValue), [calendarContextValue]);
    return (React.createElement(QueryClientProvider, { client: qc },
        React.createElement(TimeZoneContext.Provider, { value: tzContext },
            React.createElement(TenantContext.Provider, { value: tenantCtx },
                React.createElement(ModalContext.Provider, { value: modalCtx },
                    React.createElement(CalendarContext.Provider, { value: calendarCtx }, children))))));
}
